import {CSSProperties} from "react";
import "./Buttons.scss";
import {Button, IconProps, SpinnerSize} from "@vebox/etui-core";
import {Loader} from "../Utils/loader/Loader";
import {StyleProp} from "@vebox/etui-system";
import {themeDefaultValues} from "../../index";
import React = require("react");

export namespace Buttons {

    const ButtonLoader = () => {
        return (
            <div
                className="image-container"
            >
                <Loader spinnerSize={SpinnerSize.small} relativelyCentered={true}/>
            </div>
        );
    };

    interface CommonButtonProps {
        onClick: (key: string) => any;
        key?: any;
        code?: any;
        selected?: boolean;
        svgName?: IconProps["glyph"];
        onActive?: () => any;
        disabled?: boolean;
        variant?: 'default' | 'strong' | 'primary' | 'secondary' | 'tertiary';
        id?: string;
        className?: string;
        name?: string;
        label?: string;
        loading?: boolean;
        height?: string;
        width?: string;
        invertIconColor?: boolean;
        backgroundColor?: string;
        hoverColor?: string;
        borderColor?: string;
        title?: string;
        styles?: {
            root?: StyleProp;
            text?: StyleProp;
        };
    }

    interface IconButtonProps extends CommonButtonProps {
        icon?: string;
    }

    interface SvgButtonProps extends CommonButtonProps {
        svgName: IconProps["glyph"];
    }

    interface ComponentButtonProps extends CommonButtonProps {
        frontComponent?: any;
    }

    interface HACCPButtonWithIconProps extends IconButtonProps {
        icon: string;
        showBorder?: boolean;
    }

    interface ButtonExportToExcelProps {
        loading?: boolean;
        onClick?: () => any;
        variant?: 'default' | 'strong' | 'primary' | 'secondary' | 'tertiary';
        invertIconColor?: boolean;
        label?: string;
    }

    export const ButtonWithIcon: React.FunctionComponent<IconButtonProps> = (props: IconButtonProps) => {

        const handleClick = () => {
            if (props.disabled) {
                return;
            }
            props.onClick(props.code ?? null);
        };

        let localInvertIconColor = false;
        if (props.icon && props.variant === "tertiary" && props.invertIconColor === null) {
            localInvertIconColor = true;
        }

        const defaultButtonWithIconStyles = {
            width: props.width || "42px",
            height: props.height || "42px",
            m: "auto",
            p: "0",
            display: "block",
        }

        const defaultButtonWithLabelStyles = {
            width: "unset",
            height: "unset",
            fontFamily: "Myriad pro",
        }

        const thisButtonStyles = props.label ? defaultButtonWithLabelStyles : defaultButtonWithIconStyles;


        const container = props.loading ?
            <ButtonLoader/> :
            <Button
                id={props.id}
                onClick={handleClick}
                className={`image-container ${props.className ?? ""}`}
                variant={props.variant ?? "secondary"}
                title={props.title}
                frontComponent={
                    props.icon && <img
                        src={props.icon}
                        alt={props.icon}
                        style={{
                            maxHeight: "100%",
                            maxWidth: "100%",
                            filter: props.invertIconColor || localInvertIconColor ? "invert(.6)" : (props.disabled ? "brightness(.7)" : "invert(0)") /* TODO: very sloppy fix, find a cleaner way to do it*/
                        }}
                    />
                }
                iconProps={props.svgName && {glyph: props.svgName, size: props.label ? 24 : 32}}
                shape={props.label ? "default" : "circle"}
                disabled={props.disabled ?? false}
                styles={{
                    root: props.styles ?
                        {...thisButtonStyles, ...props.styles.root} :
                        thisButtonStyles
                }}
            >
                {props.label}
            </Button>;
        const className = "button-with-icon";
        const buttonClasses: string[] = [className];
        if (props.selected) {
            buttonClasses.push(className + "--selected");
        }
        return (
            <div className={buttonClasses.join(" ")}>
                {container}
                {
                    props.name ??
                    <span style={{margin: "0 auto"}} className="image-container__label"> {props.name} </span>
                }
            </div>
        );
    };

    export const ButtonWithSvg: React.FunctionComponent<SvgButtonProps> = (props: SvgButtonProps) => {
        const svgButtonStyles = {
            display: "flex",
            m: "unset",
            p: "unset",
        }

        return <ButtonWithIcon
            styles={{root: svgButtonStyles}}
            {...props}
        />
    }

    export const ExportToExcel: React.SFC<ButtonExportToExcelProps> = (props: ButtonExportToExcelProps) => {
        return <ButtonWithIcon
            loading={props.loading}
            onClick={props.onClick}
            icon="./images/buttons/icon-excelLogo.png"
            label={props.label ? props.label : null}
            variant={props.variant}
            invertIconColor={props.invertIconColor}
        />
    };

    export const ComponentButton: React.FunctionComponent<ComponentButtonProps> = (props: ComponentButtonProps) => {

        const handleClick = () => {
            if (props.disabled) {
                return;
            }
            props.onClick(props.code ?? null);
        };

        /*let localInvertIconColor = false;
        if (props.icon && props.variant === "tertiary") {
            localInvertIconColor = true;
        }*/

        const defaultButtonStyles = {
            width: props.width || "42px",
            height: props.height || "42px",
            display: "block",
            m: "auto",
            p: "0",
        }

        const container = props.loading ?
            <ButtonLoader/> :
            <Button
                id={props.id}
                onClick={handleClick}
                className={`image-container ${props.className ?? ""}`}
                variant={props.variant ?? "secondary"}
                frontComponent={
                    props.frontComponent
                }
                iconProps={{glyph: props.svgName}}
                shape="circle"
                disabled={props.disabled ?? false}
                styles={{
                    root: props.styles ? {...defaultButtonStyles, ...props.styles.root} : defaultButtonStyles
                }}
            >
                {props.label}
            </Button>;
        const className = "button-with-icon";
        const buttonClasses: string[] = [className];
        if (props.selected) {
            buttonClasses.push(className + "--selected");
        }
        return (
            <div className={buttonClasses.join(" ")}>
                {container}
                {
                    props.name ??
                    <span style={{margin: "0 auto"}} className="image-container__label"> {props.name} </span>
                }
            </div>
        );
    }

    export const LegacyHACCPButtonWithIcon: React.FunctionComponent<HACCPButtonWithIconProps> =
        (props: HACCPButtonWithIconProps) => {

            const handleClick = () => {
                if (props.disabled) {
                    return;
                }
                props.onClick(props.code);
            };

            const toolIconStyle: CSSProperties = {
                backgroundImage: "url(" + props.icon + ")",
            };
            const container = props.loading
                ? <ButtonLoader/>
                : (
                    <div
                        className="image-container"
                        onClick={handleClick}
                        style={{
                            borderRadius: "50%",
                            backgroundColor: themeDefaultValues.buttonBg,
                        }}
                    >
                        <div
                            id={props.id}
                            className="image-container__icon"
                            style={{
                                ...toolIconStyle,
                                backgroundSize: "contain",
                            }}
                        />
                    </div>
                );
            const className = "button-with-icon";
            const buttonClasses: string[] = [className];
            if (props.disabled) {
                buttonClasses.push(className + "--disabled");
            } else if (props.selected) {
                buttonClasses.push(className + "--selected");
            }
            return (
                <div className={buttonClasses.join(" ")}>
                    {container}
                    {
                        !props.name ? null
                            : <p className="image-container__label"> {props.name}</p>
                    }
                </div>
            );
        };

    export const LegacyHACCPExportToExcel: React.SFC<ButtonExportToExcelProps> = (props: ButtonExportToExcelProps) => {
        const container = props.loading
            ? <ButtonLoader/>
            : (
                <div
                    className="image-container"
                    onClick={props.onClick}
                    style={{
                        borderRadius: "50%",
                        backgroundColor: themeDefaultValues.buttonBg,
                    }}
                >
                    <div
                        className="image-container__icon"
                        style={{
                            background: "url('/images/buttons/icon-excelLogo.png') no-repeat 11px 11px",
                            backgroundSize: "contain",
                            width: themeDefaultValues.buttonWidth,
                            height: themeDefaultValues.buttonHeight,
                            backgroundPosition: "center",
                        }}
                    />
                </div>
            );
        return (
            <div className="button-export-to-excel">
                {container}
            </div>
        );
    };
}
